import * as React from "react"
import {
  type CategoryType,
  DirectionFromOrigin,
  SortDirection,
  TransactionColumnName,
  useListTransactionsQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import stringHelper from "@digits-shared/helpers/stringHelper"
import {
  TopActivity,
  TopActivityContainer,
} from "src/frontend/components/OS/Shared/Widgets/TopActivities/shared"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import { ComponentHeaderTitle } from "src/frontend/components/Shared/Layout/Components/Headers/ComponentSummary"
import { TopTransactionsList } from "src/frontend/components/Shared/Layout/Components/Lists/TopTransactionsList"
import { ComponentSize } from "src/frontend/components/Shared/Layout/ComponentSize"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

/*
  STYLES
*/

/*
  INTERFACES
*/

interface TopTransactionsProps {
  highlight: TopActivity
  categoryTypes: CategoryType[] | undefined
}

/*
  COMPONENTS
*/

export const TopTransactions: React.FC<TopTransactionsProps> = ({ highlight, categoryTypes }) => {
  const origin = useIntervalOrigin()
  const { data, loading } = useTransactionsQuery(highlight, categoryTypes)
  const periodName = dateTimeHelper.displayNameForIntervalOriginRange(origin, { delimiter: " - " })

  const className = stringHelper.classNames(highlight !== TopActivity.Largest && "hidden")
  return (
    <TopActivityContainer className={className}>
      <ComponentHeaderTitle
        title="Top Transactions"
        componentSize={ComponentSize.Small}
        periodName={periodName}
      />
      <TopTransactionsList transactions={data?.listTransactions} loading={loading} />
    </TopActivityContainer>
  )
}

/*
  HOOKS
*/
function useTransactionsQuery(highlight: TopActivity, categoryTypes?: CategoryType[]) {
  const viewKey = useViewVersion()
  const intervalOrigin = useIntervalOrigin()

  return useListTransactionsQuery({
    variables: {
      filter: {
        viewKey,
        categoryTypes,
      },
      paginationDirection: DirectionFromOrigin.Past,
      pagination: {
        limit: 5,
        offset: 0,
      },
      sortColumnName: TransactionColumnName.AbsoluteAmount,
      sortDirection: SortDirection.Descending,
      intervalOrigin: {
        ...intervalOrigin,
        intervalCount: 1,
      },
    },
  })
}
